import { Icon } from 'components/Icon';
import { classNames, React, useEffect, useIdent, useRef, useState } from 'libs/react';
import './StoreFeeInfos.scss';
/**
 * @const CLOSE_DELAY
 * @since 1.0.0
 */
const CLOSE_DELAY = 150;
/**
 * Info button of a fee in the store summary. The popover renders the fee's
 * rich text below the row: hover opens it, a click keeps it open.
 *
 * @component StoreFeeInfos
 * @since 1.0.0
 */
export function StoreFeeInfos(props) {
    const { infos, label } = props;
    const [ident] = useIdent('store-fee-infos');
    const [hovered, setHovered] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [arrowLeft, setArrowLeft] = useState(0);
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const closeTimer = useRef(null);
    const open = hovered || pinned;
    /**
     * Point the arrow at the button. The popover is positioned against the
     * fee row, which is the button's offset parent.
     */
    useEffect(() => {
        const button = buttonRef.current;
        if (open && button) {
            setArrowLeft(button.offsetLeft + button.offsetWidth / 2);
        }
    }, [open]);
    useEffect(() => {
        if (!pinned) {
            return;
        }
        function onPointerDown(event) {
            var _a;
            if (!((_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                setPinned(false);
                setHovered(false);
            }
        }
        function onKeyDown(event) {
            var _a;
            if (event.key === 'Escape') {
                setPinned(false);
                setHovered(false);
                (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [pinned]);
    useEffect(() => () => cancelClose(), []);
    /**
     * @function cancelClose
     * @since 1.0.0
     */
    function cancelClose() {
        if (closeTimer.current !== null) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }
    /**
     * @function onMouseEnter
     * @since 1.0.0
     */
    function onMouseEnter() {
        cancelClose();
        setHovered(true);
    }
    /**
     * The delay lets the pointer travel from the button to the popover.
     *
     * @function onMouseLeave
     * @since 1.0.0
     */
    function onMouseLeave() {
        cancelClose();
        closeTimer.current = window.setTimeout(() => setHovered(false), CLOSE_DELAY);
    }
    /**
     * @function onClick
     * @since 1.0.0
     */
    function onClick() {
        if (pinned) {
            setPinned(false);
            setHovered(false);
        }
        else {
            setPinned(true);
        }
    }
    return (React.createElement("span", { ref: rootRef, className: classNames('store-fee-infos', {
            'store-fee-infos--open': open
        }), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave },
        React.createElement("button", { ref: buttonRef, type: "button", className: "store-fee-infos__button", "aria-label": t('More information about {{fee}}').replace('{{fee}}', label), "aria-expanded": open, "aria-controls": ident, onClick: onClick },
            React.createElement(Icon, { className: "store-fee-infos__icon", value: "info-circle" })),
        React.createElement("span", { id: ident, role: "tooltip", className: "store-fee-infos__popover", style: { '--store-fee-infos-arrow': `${arrowLeft}px` }, "aria-hidden": !open },
            React.createElement("span", { className: "store-fee-infos__content", dangerouslySetInnerHTML: { __html: infos } }))));
}
